import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FlowCanvas } from '../../components/Canvas/FlowCanvas';
import { canvasService } from '../../services/canvasService';
import { BillingService, type SubscriptionSnapshotDto } from '../../services/BillingService';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { UserMenu } from '../../components/Layout/UserMenu';
import { PresenceIndicator } from '../../components/Canvas/PresenceIndicator';
import { ShareModal } from '../../components/Canvas/ShareModal';
import { usePresence } from '../../hooks/usePresence';
import { useAuthStore } from '../../stores/authStore';
import { useOrgStore } from '../../stores/orgStore';
import { Share2 } from 'lucide-react';
import { CreditsBadge } from '../../components/Credits/CreditsBadge';

export function CanvasPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgId = useOrgStore((s) => s.activeOrgId);
  const user = useAuthStore((s) => s.user);
  const presenceUsers = usePresence(id || '', user?.id || '', user?.fullName || user?.email || '');
  const [canvasName, setCanvasName] = useState('Canvas');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isSavingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSnapshotDto | null>(null);
  const billingService = useMemo(() => new BillingService(), []);
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    if (id && orgId) {
      setIsLoading(true);
      setError(null);
      canvasService.get(id)
        .then(({ data }) => {
          setCanvasName(data.name || 'Untitled Canvas');
        })
        .catch(() => {
          setError('Canvas not found');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, orgId]);

  useEffect(() => {
    billingService.getSubscription().then(setSubscription).catch(() => {});
  }, [billingService]);

  const isSubscriptionInactive = subscription != null && !['active', 'trialing'].includes(subscription.status);

  const startEditing = () => {
    setEditValue(canvasName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const saveCanvasName = async () => {
    if (isSavingRef.current) return;
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === canvasName) {
      setIsEditing(false);
      return;
    }
    const previousName = canvasName;
    setCanvasName(trimmed);
    setIsEditing(false);
    if (id) {
      isSavingRef.current = true;
      try {
        await canvasService.update(id, { name: trimmed });
      } catch {
        setCanvasName(previousName);
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-4 h-11 md:h-12 flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          <Link
            to="/dashboard"
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-xs font-medium hidden md:inline">Dashboard</span>
          </Link>
          <div className="h-4 w-px bg-gray-200" />
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveCanvasName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCanvasName();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              className="text-sm font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5 outline-none focus:border-primary-500 min-w-0 w-full max-w-[200px]"
              autoFocus
            />
          ) : (
            <button
              onClick={startEditing}
              className="text-sm font-semibold text-gray-800 truncate hover:bg-gray-100 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
              title="Tap to rename"
            >
              {canvasName}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <PresenceIndicator users={presenceUsers} />
          <button
            onClick={() => setIsShareOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            title="Share canvas"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Share</span>
          </button>
          <CreditsBadge />
          <UserMenu />
        </div>
      </div>

      {/* Subscription warning banner */}
      {isSubscriptionInactive && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm text-amber-700">
              {subscription?.status === 'canceled'
                ? 'Your subscription has been canceled. Generations are disabled.'
                : subscription?.status === 'past_due'
                  ? 'Your payment is past due. Please update your payment method.'
                  : 'Your subscription is inactive. Generations are disabled.'}
            </span>
          </div>
          <Button size="sm" variant="primary" onClick={() => navigate('/onboarding')}>
            {subscription?.status === 'past_due' ? 'Update Payment' : 'Reactivate'}
          </Button>
        </div>
      )}

      {/* Canvas Area */}
      <div className="flex-1 relative">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-lg font-medium text-gray-600 mb-2">Canvas not found</h2>
            <p className="text-sm text-gray-400 mb-4">This canvas may have been deleted or you don't have access.</p>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        ) : (
          <FlowCanvas canvasId={id!} presenceUsers={presenceUsers} />
        )}
      </div>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        canvasId={id!}
        canvasName={canvasName}
      />
    </div>
  );
}
